import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GoneNotice from "@/components/seo/GoneNotice";
import SEO from "@/components/SEO";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Star, Phone, Globe, ArrowRight, Building2, Users, Award, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import RatingBadge from "@/components/RatingBadge";
import { generateLocalBusinessSchema, generateBreadcrumbSchema } from "@/utils/seo";
import { SITE_URL } from "@/constants/seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { buildPostalAddressStrict } from "@/utils/structuredData/postalAddress";
import { RelatedInterlinks } from "@/components/interlinking/RelatedInterlinks";

const ITEMS_PER_PAGE = 15;

interface CityStats {
  totalListings: number;
  avgRating: number;
  topCategories: string[];
}

const CityLanding = () => {
  const { citySlug } = useParams<{ citySlug: string }>();
  const navigate = useNavigate();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  
  const [listings, setListings] = useState<any[]>([]);
  const [allListings, setAllListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [cityName, setCityName] = useState("");
  const [cityStats, setCityStats] = useState<CityStats>({
    totalListings: 0,
    avgRating: 0,
    topCategories: []
  });
  const [filterType, setFilterType] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("rating");
  const [mapboxToken, setMapboxToken] = useState<string>("");

  // Fetch Mapbox token with caching and error handling
  useEffect(() => {
    let isMounted = true;
    
    const fetchMapboxToken = async () => {
      // Check localStorage cache first
      const cachedToken = localStorage.getItem('mapbox_token');
      const cacheTime = localStorage.getItem('mapbox_token_time');
      
      // Use cache if less than 24 hours old
      if (cachedToken && cacheTime) {
        const hoursSinceCache = (Date.now() - parseInt(cacheTime)) / (1000 * 60 * 60);
        if (hoursSinceCache < 24) {
          setMapboxToken(cachedToken);
          return;
        }
      }
      
      try {
        const { data, error } = await supabase.functions.invoke('get-mapbox-token', {
          method: 'GET',
        });
        
        if (!isMounted) return;
        
        if (!error && data?.token) {
          setMapboxToken(data.token);
          // Cache the token
          localStorage.setItem('mapbox_token', data.token);
          localStorage.setItem('mapbox_token_time', Date.now().toString());
        } else {
          console.error('Error fetching Mapbox token:', error);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching Mapbox token:', error);
        }
      }
    };
    
    fetchMapboxToken();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch city data and listings
  useEffect(() => {
    if (!citySlug) return;
    
    fetchCityData();
  }, [citySlug]);

  // Apply filters and sorting
  useEffect(() => {
    let filtered = [...allListings];
    
    // Apply type filter
    if (filterType !== "all") {
      filtered = filtered.filter(listing => listing.type === filterType);
    }
    
    // Apply sorting
    switch (sortBy) {
      case "rating":
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case "name":
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "newest":
        filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
    }
    
    setListings(filtered);
    setCurrentPage(1);
  }, [filterType, sortBy, allListings]);

  // Initialize map with optimization
  useEffect(() => {
    if (!mapboxToken || !mapContainer.current || !cityName || listings.length === 0) return;
    
    if (map.current) return; // Initialize map only once
    
    mapboxgl.accessToken = mapboxToken;
    
    // Get center from listings with coordinates
    const listingsWithCoords = listings.filter(l => l.latitude && l.longitude);
    
    if (listingsWithCoords.length === 0) {
      console.warn('No listings with coordinates found');
      return;
    }
    
    // Calculate center from all coordinates
    const avgLat = listingsWithCoords.reduce((sum, l) => sum + l.latitude, 0) / listingsWithCoords.length;
    const avgLng = listingsWithCoords.reduce((sum, l) => sum + l.longitude, 0) / listingsWithCoords.length;
    
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11', // Lighter, faster style
      center: [avgLng, avgLat],
      zoom: 12,
      attributionControl: false, // Remove attribution for cleaner look
      renderWorldCopies: false, // Optimize performance
    });
    
    // Add compact controls
    map.current.addControl(
      new mapboxgl.NavigationControl({ 
        showCompass: false // Simpler controls
      }), 
      'top-right'
    );
    
    // Wait for map to load before adding markers
    map.current.on('load', () => {
      if (!map.current) return;
      
      // Clear any existing markers
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      
      // Limit markers to top 50 listings for performance
      const topListings = listingsWithCoords.slice(0, 50);
      
      topListings.forEach(listing => {
        const popup = new mapboxgl.Popup({ 
          offset: 25,
          closeButton: false,
          maxWidth: '250px',
          className: 'custom-popup'
        }).setHTML(
          `<div class="p-3">
            <h3 class="font-bold text-sm mb-1">${listing.name}</h3>
            ${listing.rating ? `<div class="flex items-center gap-1 mb-1">
              <span class="text-yellow-500">★</span>
              <span class="text-xs font-medium">${listing.rating}</span>
            </div>` : ''}
            <p class="text-xs text-gray-600 mb-2">${listing.address || listing.city}</p>
            <a href="/listing/${listing.slug || listing.id}" class="text-primary text-xs font-medium hover:underline">Ver detalles →</a>
          </div>`
        );
        
        const marker = new mapboxgl.Marker({ 
          color: '#8B5CF6',
          scale: 0.8 // Slightly smaller markers
        })
          .setLngLat([listing.longitude, listing.latitude])
          .setPopup(popup)
          .addTo(map.current!);
        
        markersRef.current.push(marker);
      });
      
      // Fit map to show all markers with padding
      if (topListings.length > 1) {
        const bounds = new mapboxgl.LngLatBounds();
        topListings.forEach(listing => {
          bounds.extend([listing.longitude, listing.latitude]);
        });
        map.current?.fitBounds(bounds, { 
          padding: { top: 50, bottom: 50, left: 50, right: 50 },
          maxZoom: 14,
          duration: 1000 // Smooth animation
        });
      }
    });
    
    return () => {
      // Clean up markers
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      // Clean up map
      map.current?.remove();
      map.current = null;
    };
  }, [mapboxToken, cityName, listings.length]); // Only depend on listings.length, not full array

  const fetchCityData = async () => {
    setLoading(true);
    
    try {
      // Normalize city slug - convert to title case
      const normalizedCity = citySlug!
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      // Function to remove accents for matching
      const removeAccents = (str: string) => {
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      };
      
      // Fetch ALL active listings first
      const { data: allListingsData, error: fetchError } = await supabase
        .from("listings")
        .select("*")
        .eq("status", "active")
        .order("rating", { ascending: false });
      
      if (fetchError) throw fetchError;
      
      // Filter listings by normalized city name (case and accent insensitive)
      const listingsData = allListingsData?.filter(listing => {
        if (!listing.city) return false;
        const listingCityNormalized = removeAccents(listing.city.toLowerCase());
        const searchCityNormalized = removeAccents(normalizedCity.toLowerCase());
        return listingCityNormalized === searchCityNormalized;
      });
      
      if (!listingsData || listingsData.length === 0) {
        setNotFound(true);
        return;
      }
      
      setAllListings(listingsData);
      setListings(listingsData);
      setCityName(listingsData[0].city);
      
      // Calculate stats
      const total = listingsData.length;
      const avgRating = listingsData.reduce((sum, l) => sum + (l.rating || 0), 0) / total;
      const typeCounts: Record<string, number> = {};
      listingsData.forEach(l => {
        typeCounts[l.type] = (typeCounts[l.type] || 0) + 1;
      });
      const topCategories = Object.entries(typeCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([type]) => type);
      
      setCityStats({
        totalListings: total,
        avgRating: Number(avgRating.toFixed(1)),
        topCategories
      });
      
    } catch (error) {
      console.error("Error fetching city data:", error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  // Pagination
  const totalPages = Math.ceil(listings.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentListings = listings.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": `Gimnasios y Centros Fitness en ${cityName}`,
      "description": `Directorio completo de ${cityStats.totalListings} gimnasios, centros fitness y instalaciones deportivas en ${cityName}`,
      "numberOfItems": cityStats.totalListings,
      "itemListElement": currentListings.slice(0, 10).map((listing, index) => ({
        "@type": "ListItem",
        "position": startIndex + index + 1,
        "item": {
          "@type": "LocalBusiness",
          "name": listing.name,
          "url": `${SITE_URL}/listing/${listing.slug || listing.id}`,
          "image": listing.thumbnail_url || listing.images?.[0],
          ...(buildPostalAddressStrict({
            streetAddress: listing.address,
            addressLocality: listing.city,
            postalCode: listing.postal_code,
            addressRegion: listing.region,
            addressCountry: "ES",
          })
            ? {
                address: buildPostalAddressStrict({
                  streetAddress: listing.address,
                  addressLocality: listing.city,
                  postalCode: listing.postal_code,
                  addressRegion: listing.region,
                  addressCountry: "ES",
                }),
              }
            : {}),
          ...(listing.rating && listing.rating > 0 ? {
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": listing.rating.toString(),
              "reviewCount": 1,
              "bestRating": "5",
              "worstRating": "1"
            }
          } : {})
        }
      }))
    },
    generateBreadcrumbSchema([
      { name: "Inicio", url: SITE_URL },
      { name: "Directorio", url: `${SITE_URL}/directory` },
      { name: cityName, url: `${SITE_URL}/ciudad/${citySlug}` }
    ])
  ];

  if (notFound) {
    return (
      <GoneNotice
        title="Ciudad sin centros publicados"
        description="Todavía no tenemos centros verificados publicados en esta ciudad. Consulta el directorio completo para encontrar opciones cercanas."
        backTo="/directory"
        backLabel="Ver el directorio"
      />
    );
  }

  if (loading) {
    return (
      <>
        <Navigation />
        <main className="pt-32 lg:pt-40 pb-16 min-h-screen">
          <div className="container mx-auto px-4 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Cargando instalaciones...</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO
        title={`${cityStats.totalListings} Gimnasios y Centros Fitness en ${cityName} - Guía Completa 2025`}
        description={`✓ Descubre los mejores ${cityStats.totalListings} gimnasios en ${cityName}. Valoración promedio ${cityStats.avgRating}⭐. Mapa interactivo, reseñas reales y ofertas exclusivas. ¡Encuentra tu gimnasio ideal!`}
        keywords={[
          `gimnasios ${cityName}`,
          `centros fitness ${cityName}`,
          `gimnasios cerca de mi ${cityName}`,
          `crossfit ${cityName}`,
          `yoga ${cityName}`,
          `pilates ${cityName}`,
          `entrenamiento personal ${cityName}`,
          `instalaciones deportivas ${cityName}`,
          ...cityStats.topCategories
        ]}
        structuredData={structuredData}
        ogImage={currentListings[0]?.thumbnail_url || "/og-image.png"}
      />
      <Navigation />
      
      <main className="pt-32 lg:pt-40 pb-16 min-h-screen">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Header Section */}
          <header className="mb-12">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Link to="/" className="hover:text-primary">Inicio</Link>
              <span>/</span>
              <Link to="/directory" className="hover:text-primary">Directorio</Link>
              <span>/</span>
              <span className="text-foreground">{cityName}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Gimnasios en {cityName}
            </h1>
            <p className="text-xl text-muted-foreground mb-6">
              Descubre {cityStats.totalListings} gimnasios y centros fitness en {cityName}
            </p>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Card>
                <CardContent className="p-6 text-center">
                  <Building2 className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <div className="text-3xl font-bold">{cityStats.totalListings}</div>
                  <div className="text-sm text-muted-foreground">Instalaciones</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <Star className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                  <div className="text-3xl font-bold">{cityStats.avgRating}</div>
                  <div className="text-sm text-muted-foreground">Valoración Media</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <Award className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <div className="text-3xl font-bold">{cityStats.topCategories.length}</div>
                  <div className="text-sm text-muted-foreground">Categorías Top</div>
                </CardContent>
              </Card>
            </div>
          </header>

          {/* Map Section */}
          <section className="mb-12" aria-label="Mapa interactivo de centros deportivos">
            <Card className="overflow-hidden">
              <CardContent className="p-0 relative">
                <div ref={mapContainer} className="w-full h-[450px] md:h-[500px]" />
                {!mapboxToken && (
                  <div className="absolute inset-0 flex items-center justify-center bg-muted/80">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                      <p className="text-sm text-muted-foreground">Cargando mapa...</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          {/* Filters and Sorting */}
          <div className="flex flex-wrap gap-4 mb-8 items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm font-medium">Filtros:</span>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  <SelectItem value="gym">Gimnasios</SelectItem>
                  <SelectItem value="yoga">Yoga</SelectItem>
                  <SelectItem value="crossfit">CrossFit</SelectItem>
                  <SelectItem value="pilates">Pilates</SelectItem>
                  <SelectItem value="martial_arts">Artes Marciales</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Ordenar por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rating">Mejor Valorados</SelectItem>
                  <SelectItem value="name">Nombre A-Z</SelectItem>
                  <SelectItem value="newest">Más Recientes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results count */}
          <div className="mb-6">
            <p className="text-muted-foreground">
              Mostrando {startIndex + 1}-{Math.min(endIndex, listings.length)} de {listings.length} resultados
            </p>
          </div>

          {/* Listings Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {currentListings.map((listing) => (
              <Card key={listing.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <Link to={`/listing/${listing.slug || listing.id}`}>
                  <div className="aspect-video relative overflow-hidden bg-secondary/20">
                    <img
                      src={listing.is_premium 
                        ? (listing.thumbnail_url || 
                           (listing.images && listing.images.length > 0 ? listing.images[0] : null) || 
                           listing.image || 
                           "/default-listing-fallback.png")
                        : "/default-listing-fallback.png"}
                      alt={`${listing.name} - Gimnasio en ${cityName}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      width="400"
                      height="225"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = "/default-listing-fallback.png";
                      }}
                    />
                    <div className="absolute bottom-2 left-2">
                      <img
                        src={listing.profile_image || 
                             listing.logo_url || 
                             "/default-listing-fallback.png"}
                        alt={`Logo de ${listing.name}`}
                        loading="lazy"
                        width="64"
                        height="64"
                        className="w-16 h-16 rounded-full border-4 border-background object-cover shadow-lg"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = "/default-listing-fallback.png";
                        }}
                      />
                    </div>
                    {listing.is_premium && (
                      <Badge className="absolute top-2 right-2 bg-gradient-primary">
                        Premium
                      </Badge>
                    )}
                  </div>
                </Link>
                
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-bold text-lg line-clamp-2 flex-1">
                      <Link to={`/listing/${listing.slug || listing.id}`} className="hover:text-primary">
                        {listing.name}
                      </Link>
                    </h3>
                    {listing.rating && <RatingBadge rating={listing.rating} />}
                  </div>
                  
                  {listing.address && (
                    <div className="flex items-start gap-2 text-sm text-muted-foreground mb-3">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-2">{listing.address}</span>
                    </div>
                  )}
                  
                  {listing.short_description && (
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {listing.short_description}
                    </p>
                  )}
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {listing.tags?.slice(0, 3).map((tag: string, idx: number) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <Button 
                      asChild 
                      className="w-full bg-gradient-primary text-primary-foreground hover:opacity-90 transition-opacity"
                    >
                      <Link to={`/listing/${listing.slug || listing.id}`}>
                        Ver Perfil
                      </Link>
                    </Button>
                    <Button 
                      asChild
                      variant="default"
                      className="w-full"
                    >
                      <Link to="/submit-listing">
                        Registrar como {listing.type || 'Negocio'}
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="flex justify-center items-center gap-2" aria-label="Paginación">
              <Button
                variant="outline"
                size="icon"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // Show first, last, current, and pages around current
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      onClick={() => goToPage(page)}
                      className="min-w-[40px]"
                    >
                      {page}
                    </Button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page}>...</span>;
                }
                return null;
              })}
              
              <Button
                variant="outline"
                size="icon"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </nav>
          )}

          {/* CTA Section */}
          <section className="mt-16 text-center p-12 bg-gradient-primary rounded-lg">
            <h2 className="text-3xl font-bold text-white mb-4">
              ¿Tienes un gimnasio en {cityName}?
            </h2>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Únete a Ekselencia y llega a miles de clientes potenciales en {cityName}
            </p>
            <Button size="lg" className="bg-gradient-primary hover:opacity-90 transition-opacity" asChild>
              <Link to="/submit-listing">
                Registrar Mi Gimnasio
              </Link>
            </Button>
          </section>

          {/* Related Interlinks */}
          <div className="container mx-auto px-4 pb-8">
            <RelatedInterlinks 
              text={`gimnasios centros fitness ${cityName} entrenamiento personal crossfit yoga pilates ${cityStats.topCategories.join(' ')}`} 
              maxLinks={6} 
            />
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default CityLanding;
